// src/components/ThirdPartyContact/Components/ThirdPartyContactDataGrid.js
import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { fetchThirdPartyContacts } from "../../../../store/ThirdPartyContacts/thirdPartyContactsThunks";
import { getThirdPartyContactColumns } from "./ThirdPartyContactDataGridColumns";
import ThirdPartyContactCustomToolbar from "./ThirdPartyContactCustomToolbar";

function ThirdPartyContactDataGrid({ 
  onEditContact, 
  onAddContact, 
  searchQuery, 
  onSearch, 
  apiRef 
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { contacts, loading, currentPage, hasMore } = useSelector(
    (state) => state.thirdPartyContacts
  );
  const containerRef = useRef(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  // Initial load: get the first page.
  useEffect(() => {
    dispatch(fetchThirdPartyContacts({ page: 1, search: searchQuery }));
  }, [dispatch, searchQuery]);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    dispatch(
      fetchThirdPartyContacts({
        page: 1,
        filters: filterModel.items.filter(
          (filter) => filter.value !== undefined && filter.value !== ""
        ),
        sortModel: newSortModel,
        search: searchQuery,
      })
    );
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const validFilters = newFilterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    if (validFilters.length > 0) {
      dispatch(
        fetchThirdPartyContacts({
          page: 1,
          filters: validFilters,
          sortModel: sortModel,
          search: searchQuery,
        })
      );
    }
  };

  const handleRefresh = () => {
    dispatch(fetchThirdPartyContacts({ page: 1, search: searchQuery }));
  };

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      dispatch(fetchThirdPartyContacts({ 
        page: currentPage + 1,
        search: searchQuery,
      }));
    }
  }, [dispatch, loading, hasMore, currentPage, searchQuery]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <Box sx={{ height: "100%" }} ref={containerRef}>
      <DataGridPro
        apiRef={apiRef}
        rows={contacts}
        columns={getThirdPartyContactColumns(onEditContact)}
        disableExtendRowFullWidth
        sortingMode="server"
        filterMode="server"
        filterDebounceMs={500}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        loading={loading}
        autoHeight={false}
        hideFooter
        initialState={{
          columns: {
            columnVisibilityModel: {
              // Customize column visibility as needed
            },
          },
        }}
        slotProps={{
          filterPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiButton-root': {
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              },
              '& .MuiIconButton-root': {
                color: theme.palette.text.primary,
              },
            }
          },
          columnsPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
        sx={{
          width: "100%",
          "& .MuiDataGrid-detailPanelToggleCell": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
            display: "flex !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: (theme) => theme.palette.background.paper,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
          "& .MuiDataGrid-main": {
            width: "100%",
          },
        }}
      />
    </Box>
  );
}

export default ThirdPartyContactDataGrid;
